import { useContext, useState } from "react";
import {
  Users,
  Music,
  Megaphone,
  Sparkles,
  Flag,
  Siren,
  Bell,
  BellOff,
  AlertCircle,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, getDeviceId, DataContext } from "@/App";

const iconMap = {
  Users,
  Music,
  Megaphone,
  Sparkles,
  Flag,
  Siren,
  AlertCircle,
};

export const TimelineItem = ({ event, isSubscribed }) => {
  const { refreshData } = useContext(DataContext);
  const [loading, setLoading] = useState(false);
  const Icon = iconMap[event.icon] || AlertCircle;

  const energyColors = {
    high: { bg: "bg-red-100", text: "text-red-600", border: "border-red-200" },
    medium: { bg: "bg-yellow-100", text: "text-yellow-600", border: "border-yellow-200" },
    low: { bg: "bg-green-100", text: "text-green-600", border: "border-green-200" },
  };

  const colors = energyColors[event.energy_level] || energyColors.medium;

  const handleNotifyToggle = async () => {
    setLoading(true);
    const deviceId = getDeviceId();

    try {
      if (isSubscribed) {
        await axios.delete(`${API}/subscriptions/${event.id}?device_id=${deviceId}`);
        toast.success("Notification disabled");
      } else {
        await axios.post(`${API}/subscriptions`, {
          event_id: event.id,
          device_id: deviceId,
          minutes_before: 5,
        });
        toast.success("You'll be notified 5 minutes before this event");
        
        // Request browser notification permission
        if ("Notification" in window && Notification.permission === "default") {
          Notification.requestPermission();
        }
      }
      refreshData();
    } catch (e) {
      console.error("Error toggling subscription:", e);
      toast.error("Failed to update notification");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes) => {
    if (minutes === 0) return "At Event Start";
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m before` : `${hours}h before`;
    }
    return `${minutes} min before`;
  };

  return (
    <article
      className="timeline-item"
      data-testid={`timeline-event-${event.id}`}
      aria-label={`${event.name} - ${formatTime(event.time_before_event)}`}
    >
      <div
        className={`timeline-icon ${colors.bg} ${colors.border} border`}
        aria-hidden="true"
      >
        <Icon size={24} className={colors.text} />
      </div>
      
      <div className="timeline-content flex-1">
        <div className="flex justify-between items-start gap-2">
          <div>
            <h3
              className="font-semibold text-base"
              style={{ fontFamily: "Lexend, sans-serif" }}
            >
              {event.name}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {formatTime(event.time_before_event)}
            </p>
          </div>
          
          <button
            className={`notify-toggle touch-target ${isSubscribed ? "active" : ""}`}
            onClick={handleNotifyToggle}
            disabled={loading}
            aria-label={isSubscribed ? "Disable notification" : "Enable notification"}
            aria-pressed={isSubscribed}
            data-testid={`notify-toggle-${event.id}`}
          >
            {isSubscribed ? (
              <>
                <Bell size={16} />
                <span>On</span>
              </>
            ) : (
              <>
                <BellOff size={16} />
                <span>Notify</span>
              </>
            )}
          </button>
        </div>
        
        <p className="text-sm text-muted-foreground mt-2">
          {event.description}
        </p>
        
        <div className="flex items-center gap-2 mt-3">
          <span
            className={`text-xs px-2 py-1 rounded-full ${colors.bg} ${colors.text}`}
          >
            {event.warning_type.charAt(0).toUpperCase() + event.warning_type.slice(1)}
          </span>
          <span
            className={`text-xs px-2 py-1 rounded-full ${colors.bg} ${colors.text}`}
          >
            {event.energy_level.charAt(0).toUpperCase() + event.energy_level.slice(1)} Energy
          </span>
        </div>
      </div>
    </article>
  );
};
